/**
 * Controls action hooks for document state management
 *
 * These hooks dispatch document actions directly to the Zustand store via DocumentStore,
 * bypassing Redux. The DocumentStore handles:
 * 1. Injecting currentUser into actions (previously done by Redux currentUserMiddleware)
 * 2. Forwarding actions to the Zustand store's reduce() method
 *
 * The Zustand store's reduce() method notifies action listeners (ActionRecordService,
 * DocumentSaveService, PatchHandlerService) so middleware functionality is preserved.
 */
import type {IControlConfig} from "@buildwithflux/core";
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";
import type {DocumentStore} from "modules/document/state/document";

/**
 * Hook to get the setControls action
 *
 * Dispatches directly to Zustand via DocumentStore, bypassing Redux.
 *
 * @returns A function to set document controls configuration
 */
export function useSetControls(): (controls: IControlConfig[]) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (controls: IControlConfig[]) => {
            documentStore.getState().setSimulationControls(controls);
        },
        [documentStore],
    );
}

/**
 * Factory function to create controls actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch controls actions.
 *
 * @param documentStore - The DocumentStore from FluxServices
 * @returns An object with controls action methods
 */
export function createControlsActions(documentStore: DocumentStore) {
    return {
        setControls: (controls: IControlConfig[]) => {
            documentStore.getState().setSimulationControls(controls);
        },
    };
}
